/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import React from 'react'

import { BasketLineItem } from '@/components/e-commerce/basket/BasketLineItem'
import { BasketTotalsSummary } from '@/components/e-commerce/basket/BasketTotalSummary'
import { useTrackViewCart } from '@/hooks/analytics/useTrackViewCart'
import { basketItemsWithProductInfoAtom } from '@/store/basket'

/** A description of available properties for this component. */
type CheckoutBasketOverviewProps = {
  /** Can quantities be changed and items removed? (default: true). */
  allowEditing?: boolean

  /** Should the checkout button be visible? (default: true). */
  showCheckoutButton?: boolean

  /** The design variant to render. */
  variant?: 'full' | 'compact'
}

/**
 * Overview of the user's basket with several properties enabling the overview to be used in different contexts.
 * For example, disabling the basket from being edited at a certain stage of checkout, showing a more compact view etc.
 */
const CheckoutBasketOverview: React.FC<CheckoutBasketOverviewProps> = ({
  allowEditing = true,
  showCheckoutButton = true,
  variant = 'full',
}) => {
  const t = useTranslations('CheckoutBasketOverview')
  const itemsWithDetails = useAtomValue(basketItemsWithProductInfoAtom)
  useTrackViewCart()

  if (itemsWithDetails.length === 0) {
    return (
      <div className='w-full max-w-sm ml-auto p-6 border rounded-lg shadow-xs bg-gray-50 text-center'>
        <h2 className='text-xl font-semibold mb-4'>{t('basketSummary')}</h2>
        <p className='text-gray-500'>{t('emptyBasket')}</p>
      </div>
    )
  }

  return (
    <div className='space-y-8'>
      <div className='border rounded-lg shadow-xs overflow-hidden bg-white'>
        <ul className='divide-y divide-gray-200'>
          {itemsWithDetails.map((item) => (
            <BasketLineItem
              key={`${item.productId}-${item.variantId ?? 'default'}`}
              item={item}
              allowEditing={allowEditing}
              variant={variant}
            />
          ))}
        </ul>
      </div>
      <BasketTotalsSummary showCheckoutButton={showCheckoutButton} />
    </div>
  )
}

export { CheckoutBasketOverview }
